import { useState, useEffect, useRef, useContext, useCallback } from "react"
import { LoaderMedium } from "../../../components/Loaders"
import ChatMessage from "./ChatMessage"
import { InputTextarea } from "primereact/inputtextarea"
import { Button } from "primereact/button"
import { DateTime } from "luxon"
import trcss from "../../../styles/Telerecovery.module.css"
import { HubConnectionBuilder } from "@microsoft/signalr"
import { UserContext } from "../../../context/userContext"
import { telerecoveryApiUrl } from "../../../config/microservicesUrls"
import {
  trApiGetChatHistory,
  trApiSendChatMessageToStaff,
} from "../telerecoveryApi"
import {
  staffNameInitials,
  groupMessagesByDate,
  getActualUTCTime,
} from "../Utils"
import { FileUpload } from "primereact/fileupload"
import { Image } from "primereact/image"
import { Tooltip } from "primereact/tooltip"
import ExternalLinkPopup from "../ExternalLinkPopup"
import { MICROSERVICE_IDS } from "../../../helpers/microserviceConstants"

export default function ChatComponent({
  chatConvSid,
  staffId,
  staffName,
  organizationId,
  onNewMessage,
}) {
  const [messages, setMessages] = useState([])
  const [newChatText, setNewChatText] = useState("")
  const [loadingMessages, setLoadingMessages] = useState(true)
  const [sendingMessage, setSendingMessage] = useState(false)
  const [connection, setConnection] = useState(null)
  const [joinLink, setJoinLink] = useState(null)
  const [showExternalLinkPopup, setShowExternalLinkPopup] = useState(false)
  const messagesEndRef = useRef(null)
  const userCtx = useContext(UserContext)
  const [mmsFile, setMmsFile] = useState(null)

  useEffect(() => {
    const _connection = new HubConnectionBuilder()
      .withUrl(telerecoveryApiUrl() + "/notificationHub")
      .withAutomaticReconnect()
      .build()
    setConnection(_connection)
  }, [])

  const checkIncomingMessage = useCallback(
    (fromId, convSid, body) => {
      let newMsg = {
        dateUpdated: getActualUTCTime(),
        body: body,
        fromId: fromId,
        convSid: convSid,
      }
      if (onNewMessage) {
        onNewMessage(newMsg)
      }
      if (chatConvSid === convSid && fromId !== userCtx.participant_id) {
        setMessages((oldArray) => [...oldArray, newMsg])
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chatConvSid],
  )

  useEffect(() => {
    setLoadingMessages(true)
    trApiGetChatHistory(userCtx.tokens.getTelerecoveryToken(), chatConvSid)
      .then((response) => {
        const _messages = Array.from(response)
        setMessages(_messages)
        setLoadingMessages(false)
      })
      .catch((err) => {
        console.log(err)
        setLoadingMessages(false)
      })

    if (connection) {
      // Clean up previous listeners to avoid multiple subscriptions
      connection.off("IncomingChatMessage")

      // Only attempt to start the connection if it's disconnected
      if (connection.state === "Disconnected") {
        connection
          .start()
          .then(() => {
            connection.on("IncomingChatMessage", (fromId, convSid, body) => {
              checkIncomingMessage(fromId, convSid, body)
            })
          })
          .catch((e) => console.log("Connection failed: ", e))
      } else {
        // Re-subscribe to the event
        connection.on("IncomingChatMessage", (fromId, convSid, body) => {
          checkIncomingMessage(fromId, convSid, body)
        })
      }

      // Cleanup function to unregister event handlers
      return () => {
        connection.off("IncomingChatMessage")
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatConvSid, staffId, connection])

  // Ensure scrollToBottom runs after messages are updated
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom()
    }
  }, [messages])

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: "smooth" })
  }

  const sendMessage = () => {
    if ((newChatText === "" && mmsFile === null) || sendingMessage) {
      return
    }

    setSendingMessage(true)
    const data = new FormData()
    data.append("body", newChatText)
    data.append("fromId", userCtx.participant_id)
    data.append("convSid", chatConvSid)
    if (mmsFile) {
      data.append("file", mmsFile)
    }

    trApiSendChatMessageToStaff(
      userCtx.tokens.getTelerecoveryToken(),
      data,
    ).then(
      (response) => {
        let body = data.get("body")
        if (response.fileUrl && response.fileUrl !== "") {
          body += `\r\n${response.fileUrl}`
        }
        let newMsg = {
          dateUpdated: getActualUTCTime(),
          body: body,
          fromId: userCtx.participant_id,
        }
        setMessages((oldArray) => [...oldArray, newMsg])
        setSendingMessage(false)
        setNewChatText("")
        setMmsFile(null)
        //     scrollToBottom()
      },
      (err) => console.log(err),
    )
  }

  const uploadHandler = (event) => {
    const file = event.files[0]
    setMmsFile(file)
    event.options.clear()
  }

  return (
    <div className={trcss.chatWindow}>
      <ExternalLinkPopup
        link={joinLink}
        showPopup={showExternalLinkPopup}
        setShowPopup={setShowExternalLinkPopup}
      />
      <div className={trcss.chatWindowHeader}>
        {staffName && (
          <div>
            <span className={trcss.userInitials}>
              {staffNameInitials(staffName)}
            </span>
            <span
              style={{ marginLeft: "6px", fontWeight: "600", fontSize: "12px" }}
            >
              {staffName.full_name}
            </span>
          </div>
        )}
        {messages && messages.length > 0 && (
          <p className={trcss.lastMessage_text}>
            {`Last message: ${DateTime.fromISO(
              messages[messages.length - 1].dateUpdated,
            ).toRelative()}`}
          </p>
        )}
      </div>
      <div className={trcss.chatHistoryContainer}>
        {loadingMessages === true && <LoaderMedium />}
        {(!messages || messages.length === 0) && !loadingMessages && (
          <span className={trcss.convEmptyMsg}>
            This conversation is empty.
          </span>
        )}
        <ul className={trcss.chatWindowList} style={{ paddingTop: "10px" }}>
          {!loadingMessages &&
            messages &&
            groupMessagesByDate(messages).map((groupedMessages, i) => (
              <li key={"msg-" + i}>
                <ChatMessage
                  messages={groupedMessages.messages}
                  fromName={staffName}
                  setJoinLink={setJoinLink}
                  setShowExternalLinkPopup={setShowExternalLinkPopup}
                />
              </li>
            ))}
        </ul>
        <div ref={messagesEndRef}></div>
      </div>
      {mmsFile && mmsFile.objectURL && (
        <div className={trcss.mms_preview_container}>
          <Image
            src={mmsFile.objectURL}
            alt={mmsFile.name}
            width="150"
            preview
            className={trcss.mms}
          />
          <Button
            className={"p-button-rounded p-button-icon-only p-button-sm"}
            icon="pi pi-times"
            onClick={() => setMmsFile(null)}
          />
        </div>
      )}
      <div className={trcss.chatWindowFooter}>
        <InputTextarea
          placeholder="Type your message"
          autoResize
          className={trcss.newMessageBox}
          value={newChatText}
          onChange={(e) => setNewChatText(e.target.value)}
          rows={1}
          disabled={
            sendingMessage || userCtx.isParticipantInactive(organizationId)
          }
        />
        <FileUpload
          className={trcss.mms_file_upload}
          accept="image/jpeg,image/jpg,image/gif,image/png"
          maxFileSize={4000000}
          auto
          customUpload
          uploadHandler={uploadHandler}
          chooseOptions={{
            className: "p-button-icon-only",
            icon: "pi pi-paperclip",
            iconPos: "left",
          }}
          id={"fileUpload"}
          disabled={
            sendingMessage || userCtx.isParticipantInactive(organizationId)
          }
        />
        <Tooltip
          target="#fileUpload"
          className="custom-tooltip"
          position="top"
          autoHide={false}
        >
          <span>
            {userCtx.isParticipantInactive(organizationId)
              ? "This button is disabled due to your participant status"
              : "Attach an image to your message"}
          </span>
        </Tooltip>
        <Button
          className={trcss.sendBtn + " no-focus"}
          onClick={() => sendMessage()}
          loading={sendingMessage}
          disabled={
            sendingMessage ||
            userCtx.isParticipantInactive(organizationId) ||
            !userCtx.isMicroserviceEnabled(
              MICROSERVICE_IDS.TELERECOVERY_ID,
              organizationId,
            )
          }
          style={{ padding: "0px", width: "20px" }}
          id={"sendMessageBtn"}
        />
        <Tooltip
          target="#sendMessageBtn"
          className="custom-tooltip"
          position="top"
          autoHide={false}
        >
          <span>
            {userCtx.isParticipantInactive(organizationId)
              ? "This button is disabled due to your participant status"
              : "Send message"}
          </span>
        </Tooltip>
      </div>
    </div>
  )
}
